import { useMemo } from 'react';
import type {
  FormField,
  FieldType,
  FieldRule,
  RuleOperator,
  Condition,
  ConditionalLogic,
  RuleCondition,
  CalculationOperation,
} from '../types/forms';
import type { ResolvedPage } from '../lib/formPages';

/**
 * Telas que encerram o formulário e por isso não têm página própria.
 * Um desvio para elas significa "fim do fluxo" — não "segue na sequência".
 */
const END_SCREEN_TYPES: FieldType[] = ['thank_you_screen'];

// =====================================================
// TYPES
// =====================================================

export interface NextFieldMap {
  [fieldId: string]: string | null; // null = end of form
}

export interface NextPageMap {
  [pageId: string]: string | null; // null = end of form
}

export interface RuleCalculationEntry {
  variableId: string;
  operation: CalculationOperation;
  value: number;
}

export interface FlowValidationResult {
  isValid: boolean;
  hasLoops: boolean;
  orphanFields: string[];
  invalidJumps: string[];
  loopPaths: string[][];
  // Page-level results (Story 5.7) — empty arrays when form has no pages
  orphanPages: string[];
  loopPagePaths: string[][];
}

interface UseConditionalLogicProps {
  fields: FormField[];
  formData: Record<string, any>;
  /** Optional resolved pages — when provided, enables page-level branching/validation (Story 5.7). */
  pages?: ResolvedPage[];
}

interface UseConditionalLogicReturn {
  visibleFields: FormField[];
  nextFieldMap: NextFieldMap;
  nextPageMap: NextPageMap;
  ruleCalculations: RuleCalculationEntry[];
  getNextField: (currentFieldId: string) => string | null;
  getPreviousField: (navigationHistory: string[]) => string | null;
  getNextPage: (currentPageId: string) => string | null;
  flowValidation: FlowValidationResult;
  calculateProgress: (currentFieldId: string) => { current: number; total: number; percentage: number };
  calculatePageProgress: (currentPageId: string) => { current: number; total: number; percentage: number };
}

// =====================================================
// 1. CONDITION EVALUATION ENGINE
// =====================================================

function evaluateCondition(condition: Condition, formData: Record<string, any>): boolean {
  const fieldValue = formData[condition.fieldId];

  switch (condition.operator) {
    case 'equals':
      return fieldValue === condition.value;
    case 'not_equals':
      return fieldValue !== condition.value;
    case 'contains':
      if (Array.isArray(fieldValue)) return fieldValue.includes(condition.value);
      return String(fieldValue || '').includes(String(condition.value));
    case 'not_contains':
      if (Array.isArray(fieldValue)) return !fieldValue.includes(condition.value);
      return !String(fieldValue || '').includes(String(condition.value));
    case 'is_empty':
      return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
    case 'is_not_empty':
      return !!fieldValue && !(Array.isArray(fieldValue) && fieldValue.length === 0);
    case 'greater_than':
      return Number(fieldValue) > Number(condition.value);
    case 'less_than':
      return Number(fieldValue) < Number(condition.value);
    default:
      return true;
  }
}

function evaluateConditions(logic: ConditionalLogic, formData: Record<string, any>): boolean {
  // Legacy format support (backward compatible)
  if (logic.dependsOn && !logic.conditions) {
    return evaluateCondition(
      {
        fieldId: logic.dependsOn,
        operator: logic.operator || 'equals',
        value: logic.value,
      },
      formData
    );
  }

  // New format: multiple conditions with logicOperator
  if (!logic.conditions || logic.conditions.length === 0) return true;

  const results = logic.conditions.map((c) => evaluateCondition(c, formData));
  return logic.logicOperator === 'or'
    ? results.some(Boolean)
    : results.every(Boolean); // default: 'and'
}

// =====================================================
// 1b. RULE CONDITION EVALUATION (FieldRule system)
// =====================================================

function evaluateRuleCondition(condition: RuleCondition, formData: Record<string, any>): boolean {
  const fieldValue = formData[condition.fieldId];
  const compareValue = condition.value;

  // Incomplete condition (no value configured) → don't match
  if (compareValue === '' || compareValue === undefined || compareValue === null) return false;
  if (Array.isArray(compareValue) && compareValue.length === 0) return false;

  // RuleOperator é 'is' | 'is_not', mas há regras gravadas com os operadores do
  // formato legado ('equals' | 'not_equals'). O cast declara essa tolerância —
  // sem ele o `case` ficava inalcançável para o tsc e o typecheck acusava TS2678.
  switch (condition.operator as RuleOperator | Condition['operator']) {
    case 'is':
    case 'equals':
      if (Array.isArray(compareValue)) return compareValue.includes(String(fieldValue));
      return String(fieldValue ?? '') === String(compareValue);
    case 'is_not':
    case 'not_equals':
      if (Array.isArray(compareValue)) return !compareValue.includes(String(fieldValue));
      return String(fieldValue ?? '') !== String(compareValue);
    default:
      return false;
  }
}

interface ResolvedFieldRules {
  jumpToFieldId: string | null;
  jumpToPageId: string | null;
  calculations: RuleCalculationEntry[];
}

/**
 * Resolve all rules for a field.
 *
 * Precedência de desvio:
 *   1. regra condicional ('if') que casou — é a rota escolhida pelo respondente
 *   2. regra 'always' — é o fallback do nó (o FlowEditor grava a conexão default
 *      assim); nunca pode sombrear um desvio condicional que casou
 *   3. dentro do mesmo nível, jump_to_page vence jump_to (Story 5.7)
 *
 * Cálculos são coletados na ordem original do array — a ordem das operações
 * importa para multiplicação/divisão e não pode ser reordenada com os desvios.
 */
function resolveFieldRules(
  field: FormField,
  formData: Record<string, any>
): ResolvedFieldRules {
  if (!field.rules?.length) {
    return { jumpToFieldId: null, jumpToPageId: null, calculations: [] };
  }

  const calculations: RuleCalculationEntry[] = [];
  const firingRules: FieldRule[] = [];

  for (const rule of field.rules) {
    let shouldExecute = false;

    if (rule.conditionType === 'always') {
      shouldExecute = true;
    } else if (!rule.conditions || !rule.conditions.length) {
      // 'if' with no conditions → don't execute (incomplete rule setup)
      shouldExecute = false;
    } else {
      const results = rule.conditions.map(c => evaluateRuleCondition(c, formData));
      shouldExecute = rule.logicOperator === 'or'
        ? results.some(Boolean)
        : results.every(Boolean);
    }

    if (!shouldExecute) continue;

    firingRules.push(rule);

    for (const action of rule.actions) {
      if (action.type === 'calculation') {
        calculations.push({
          variableId: action.variableId,
          operation: action.operation,
          value: action.value,
        });
      }
    }
  }

  let jumpToFieldId: string | null = null;
  let jumpToPageId: string | null = null;

  // Dois passes: condicionais primeiro, 'always' só como fallback.
  for (const isAlwaysPass of [false, true]) {
    for (const rule of firingRules) {
      if ((rule.conditionType === 'always') !== isAlwaysPass) continue;
      for (const action of rule.actions) {
        if (action.type === 'jump_to_page') {
          if (!jumpToPageId) jumpToPageId = action.targetPageId;
        } else if (action.type === 'jump_to') {
          if (!jumpToFieldId) jumpToFieldId = action.targetFieldId;
        }
      }
    }
    if (jumpToPageId || jumpToFieldId) break;
  }

  return { jumpToFieldId, jumpToPageId, calculations };
}

// =====================================================
// 1c. LEGACY JUMP RESOLUTION (conditionalLogic)
// =====================================================

/**
 * Desvio no formato legado (`conditionalLogic.action === 'jump_to'`).
 * Retorna o id do campo alvo, ou null quando nenhuma condição casa.
 *
 * Extraído para ser compartilhado entre a navegação por CAMPO e a navegação por
 * PÁGINA — antes só a de campo lia o formato legado, e como toda navegação real
 * passa por páginas (resolvePages cria páginas implícitas), os formulários
 * montados no formato legado nunca desviavam.
 */
function resolveLegacyJump(
  field: FormField,
  formData: Record<string, any>
): string | null {
  const logic = field.conditionalLogic;
  if (logic?.action !== 'jump_to') return null;

  // Multi-branch: cada condição tem seu próprio alvo
  if (logic.conditions?.length) {
    for (const condition of logic.conditions) {
      if (condition.jumpTo && evaluateCondition(condition, formData)) {
        return condition.jumpTo;
      }
    }
  }

  // Branch único: alvo no nível do campo
  if (logic.jumpTo && evaluateConditions(logic, formData)) return logic.jumpTo;

  return null;
}

/** Desvio efetivo do campo: `rules` tem precedência sobre o formato legado. */
function resolveJumpTargetFieldId(
  field: FormField,
  formData: Record<string, any>,
  fromRules: string | null
): string | null {
  if (field.rules?.length) return fromRules;
  return resolveLegacyJump(field, formData);
}

// =====================================================
// 2. FIELD VISIBILITY RESOLVER
// =====================================================

function getVisibleFields(fields: FormField[], formData: Record<string, any>): FormField[] {
  return fields.filter((field) => {
    if (!field.conditionalLogic) return true;

    const logic = field.conditionalLogic;
    const action = logic.action || 'show'; // Default action for legacy format
    const conditionMet = evaluateConditions(logic, formData);

    switch (action) {
      case 'show':
        return conditionMet;
      case 'hide':
        return !conditionMet;
      case 'jump_to':
        return true; // jump_to fields are always visible (jump handled by navigation)
      default:
        return true;
    }
  });
}

// =====================================================
// 3. FIELD-LEVEL BRANCHING NAVIGATION RESOLVER
// =====================================================

function getNextFieldMapWithCalculations(
  visibleFields: FormField[],
  formData: Record<string, any>
): { map: NextFieldMap; calculations: RuleCalculationEntry[] } {
  const map: NextFieldMap = {};
  const allCalculations: RuleCalculationEntry[] = [];

  visibleFields.forEach((field, index) => {
    // NEW: Check field.rules first (takes precedence)
    if (field.rules?.length) {
      const { jumpToFieldId, calculations } = resolveFieldRules(field, formData);
      allCalculations.push(...calculations);
      if (jumpToFieldId) {
        map[field.id] = jumpToFieldId;
        return;
      }
      // jump_to_page is intentionally not consumed here — field-level navigation
      // is the legacy path used when the form has no pages. Fall through to sequential.
    }
    // EXISTING: Check for legacy conditionalLogic jump_to rules
    else {
      const legacyJump = resolveLegacyJump(field, formData);
      if (legacyJump) {
        map[field.id] = legacyJump;
        return;
      }
    }

    // Default: next visible field in sequence
    const nextField = visibleFields[index + 1];
    map[field.id] = nextField?.id || null;
  });

  return { map, calculations: allCalculations };
}

// Legacy wrapper for backward compatibility
function getNextFieldMap(
  visibleFields: FormField[],
  formData: Record<string, any>
): NextFieldMap {
  return getNextFieldMapWithCalculations(visibleFields, formData).map;
}

// =====================================================
// 3b. PAGE-LEVEL BRANCHING (Story 5.7)
// =====================================================

/**
 * Compute next-page map from resolved pages and field rules.
 *
 * Precedence (per page, walking fields in order):
 *   1. First field with a matching `jump_to_page` action wins
 *   2. Else first field with a matching `jump_to` action (de `rules` ou do formato
 *      legado `conditionalLogic`) → resolve para a página que contém o campo
 *   3. Desvio para uma tela de encerramento (thank_you_screen) → fim do fluxo
 *   4. Else next page in sequential order (or null for last page)
 *
 * Self-loops are allowed in the map (treated as warnings by validation).
 *
 * `allFields` é opcional só por compatibilidade de assinatura; sem ele os desvios
 * para a tela de agradecimento caem no sequencial em vez de encerrar o fluxo.
 */
function buildNextPageMap(
  pages: ResolvedPage[],
  formData: Record<string, any>,
  allFields?: FormField[]
): NextPageMap {
  const map: NextPageMap = {};
  if (!pages.length) return map;

  // fieldId → pageId lookup (for resolving jump_to → containing page)
  const fieldToPageId = new Map<string, string>();
  pages.forEach((rp) => {
    rp.fields.forEach((f) => fieldToPageId.set(f.id, rp.page.id));
  });

  const endScreenIds = new Set(
    (allFields || []).filter((f) => END_SCREEN_TYPES.includes(f.type)).map((f) => f.id)
  );

  pages.forEach((rp, index) => {
    const pageId = rp.page.id;
    let resolved: string | null | undefined;

    for (const field of rp.fields) {
      const { jumpToFieldId, jumpToPageId } = resolveFieldRules(field, formData);

      if (jumpToPageId) {
        resolved = jumpToPageId;
        break;
      }

      const targetFieldId = resolveJumpTargetFieldId(field, formData, jumpToFieldId);
      if (targetFieldId) {
        const targetPage = fieldToPageId.get(targetFieldId);
        if (targetPage) {
          resolved = targetPage;
          break;
        }
        if (endScreenIds.has(targetFieldId)) {
          resolved = null; // desvio para a tela de agradecimento → encerra
          break;
        }
        // Target field has no page (or unknown) → fall through to sequential
      }
    }

    if (resolved !== undefined) {
      map[pageId] = resolved;
    } else {
      const nextPage = pages[index + 1];
      map[pageId] = nextPage?.page.id ?? null;
    }
  });

  return map;
}

// =====================================================
// 4. ADAPTIVE PROGRESS CALCULATOR
// =====================================================

function calculatePathProgress(
  currentFieldId: string,
  visibleFields: FormField[],
  nextFieldMap: NextFieldMap
): { current: number; total: number; percentage: number } {
  // Walk the path from first field following nextFieldMap
  const pathFields: string[] = [];
  let walkId: string | null = visibleFields[0]?.id || null;
  const visited = new Set<string>();

  while (walkId && !visited.has(walkId)) {
    visited.add(walkId);
    pathFields.push(walkId);
    walkId = nextFieldMap[walkId];
  }

  const currentIndex = pathFields.indexOf(currentFieldId);
  const total = pathFields.length;

  // Fallback: if currentFieldId not found in path, use visibleFields count
  if (currentIndex === -1) {
    const fallbackIndex = visibleFields.findIndex((f) => f.id === currentFieldId);
    return {
      current: fallbackIndex + 1,
      total: visibleFields.length,
      percentage: visibleFields.length > 0 ? ((fallbackIndex + 1) / visibleFields.length) * 100 : 0,
    };
  }

  return {
    current: currentIndex + 1,
    total,
    percentage: total > 0 ? ((currentIndex + 1) / total) * 100 : 0,
  };
}

/**
 * Page-level analogue of calculatePathProgress — walks `nextPageMap`
 * from the first page and reports position of `currentPageId`.
 */
function calculatePagePathProgress(
  currentPageId: string,
  pages: ResolvedPage[],
  nextPageMap: NextPageMap
): { current: number; total: number; percentage: number } {
  if (!pages.length) {
    return { current: 0, total: 0, percentage: 0 };
  }

  const path: string[] = [];
  let walkId: string | null = pages[0].page.id;
  const visited = new Set<string>();

  while (walkId && !visited.has(walkId)) {
    visited.add(walkId);
    path.push(walkId);
    walkId = nextPageMap[walkId] ?? null;
  }

  const idx = path.indexOf(currentPageId);
  const total = path.length;

  if (idx === -1) {
    const fallbackIdx = pages.findIndex((p) => p.page.id === currentPageId);
    return {
      current: fallbackIdx + 1,
      total: pages.length,
      percentage: pages.length > 0 ? ((fallbackIdx + 1) / pages.length) * 100 : 0,
    };
  }

  return {
    current: idx + 1,
    total,
    percentage: total > 0 ? ((idx + 1) / total) * 100 : 0,
  };
}

// =====================================================
// 5. LOOP DETECTION & INTEGRITY VALIDATION
// =====================================================

function validateFlowIntegrity(
  fields: FormField[],
  pages?: ResolvedPage[]
): FlowValidationResult {
  const fieldIds = new Set(fields.map((f) => f.id));
  const pageIds = new Set((pages || []).map((rp) => rp.page.id));
  const result: FlowValidationResult = {
    isValid: true,
    hasLoops: false,
    orphanFields: [],
    invalidJumps: [],
    loopPaths: [],
    orphanPages: [],
    loopPagePaths: [],
  };

  // ── Validate jump targets ──
  fields.forEach((field) => {
    // Legacy conditionalLogic
    const logic = field.conditionalLogic;
    if (logic?.jumpTo && !fieldIds.has(logic.jumpTo)) {
      result.invalidJumps.push(field.id);
      result.isValid = false;
    }
    logic?.conditions?.forEach(c => {
      if (c.jumpTo && !fieldIds.has(c.jumpTo)) {
        result.invalidJumps.push(field.id);
        result.isValid = false;
      }
    });

    // field.rules — discriminated union
    field.rules?.forEach(rule => {
      rule.actions.forEach(action => {
        if (action.type === 'jump_to' && !fieldIds.has(action.targetFieldId)) {
          result.invalidJumps.push(field.id);
          result.isValid = false;
        }
        if (action.type === 'jump_to_page' && pages && !pageIds.has(action.targetPageId)) {
          // Only flag invalid page jumps when pages are defined for this form
          result.invalidJumps.push(field.id);
          result.isValid = false;
        }
      });
    });
  });

  // ── Field-level cycle detection (existing — kept for backward compat) ──
  const adjacency = new Map<string, string[]>();
  fields.forEach((field, index) => {
    const nexts: string[] = [];

    if (field.rules?.length) {
      for (const rule of field.rules) {
        for (const action of rule.actions) {
          if (action.type === 'jump_to' && fieldIds.has(action.targetFieldId) && !nexts.includes(action.targetFieldId)) {
            nexts.push(action.targetFieldId);
          }
        }
      }
    } else if (field.conditionalLogic?.action === 'jump_to') {
      if (field.conditionalLogic.jumpTo && fieldIds.has(field.conditionalLogic.jumpTo)) {
        nexts.push(field.conditionalLogic.jumpTo);
      }
      field.conditionalLogic.conditions?.forEach(c => {
        if (c.jumpTo && fieldIds.has(c.jumpTo) && !nexts.includes(c.jumpTo)) {
          nexts.push(c.jumpTo);
        }
      });
    }

    const nextField = fields[index + 1];
    if (nextField) nexts.push(nextField.id);

    adjacency.set(field.id, nexts);
  });

  const WHITE = 0, GRAY = 1, BLACK = 2;
  const color = new Map<string, number>();
  fields.forEach((f) => color.set(f.id, WHITE));

  function dfsField(nodeId: string, path: string[]): boolean {
    color.set(nodeId, GRAY);
    path.push(nodeId);

    const neighbors = adjacency.get(nodeId) || [];
    for (const next of neighbors) {
      if (color.get(next) === GRAY) {
        const cycleStart = path.indexOf(next);
        result.loopPaths.push(path.slice(cycleStart));
        result.hasLoops = true;
        // NOTE: loops are warnings, not errors — do NOT flip isValid (Story 5.7)
        return true;
      }
      if (color.get(next) === WHITE) {
        if (dfsField(next, path)) return true;
      }
    }

    path.pop();
    color.set(nodeId, BLACK);
    return false;
  }

  for (const field of fields) {
    if (color.get(field.id) === WHITE) {
      dfsField(field.id, []);
    }
  }

  // ── Page-level validation (Story 5.7) ──
  if (pages && pages.length > 0) {
    const fieldToPageId = new Map<string, string>();
    pages.forEach((rp) => {
      rp.fields.forEach((f) => fieldToPageId.set(f.id, rp.page.id));
    });

    // Build page adjacency: jump_to_page + jump_to(field→page) + sequential
    const pageAdjacency = new Map<string, Set<string>>();
    pages.forEach((rp, index) => {
      const sources: Set<string> = pageAdjacency.get(rp.page.id) || new Set<string>();

      for (const field of rp.fields) {
        field.rules?.forEach(rule => {
          rule.actions.forEach(action => {
            if (action.type === 'jump_to_page' && pageIds.has(action.targetPageId)) {
              sources.add(action.targetPageId);
            } else if (action.type === 'jump_to') {
              const targetPage = fieldToPageId.get(action.targetFieldId);
              if (targetPage) sources.add(targetPage);
            }
          });
        });
      }

      const nextPage = pages[index + 1];
      if (nextPage) sources.add(nextPage.page.id);

      pageAdjacency.set(rp.page.id, sources);
    });

    // Detect ALL page-level cycles via DFS (collect every back-edge)
    const pageColor = new Map<string, number>();
    pages.forEach((rp) => pageColor.set(rp.page.id, WHITE));

    function dfsPage(nodeId: string, path: string[]): void {
      pageColor.set(nodeId, GRAY);
      path.push(nodeId);

      const neighbors = pageAdjacency.get(nodeId) || new Set<string>();
      for (const next of neighbors) {
        if (pageColor.get(next) === GRAY) {
          const start = path.indexOf(next);
          if (start >= 0) {
            const cycle = path.slice(start);
            // Avoid duplicates (same cycle starting from a different rotation)
            const sig = [...cycle].sort().join('|');
            const exists = result.loopPagePaths.some((p) => [...p].sort().join('|') === sig);
            if (!exists) result.loopPagePaths.push(cycle);
          }
          result.hasLoops = true;
          // Loops are warnings — do NOT flip isValid (Story 5.7)
        } else if (pageColor.get(next) === WHITE) {
          dfsPage(next, [...path]);
        }
      }

      pageColor.set(nodeId, BLACK);
    }

    pages.forEach((rp) => {
      if (pageColor.get(rp.page.id) === WHITE) {
        dfsPage(rp.page.id, []);
      }
    });

    // Reachability BFS from first page → unreachable pages = orphans
    const reachable = new Set<string>();
    const queue: string[] = [pages[0].page.id];

    while (queue.length) {
      const current = queue.shift()!;
      if (reachable.has(current)) continue;
      reachable.add(current);
      const neighbors = pageAdjacency.get(current) || new Set<string>();
      neighbors.forEach((n) => {
        if (!reachable.has(n)) queue.push(n);
      });
    }

    pages.forEach((rp) => {
      if (!reachable.has(rp.page.id)) {
        result.orphanPages.push(rp.page.id);
      }
    });

    // Also populate orphanFields based on page-level analysis: fields inside orphan pages
    if (result.orphanPages.length > 0) {
      const orphanSet = new Set(result.orphanPages);
      pages.forEach((rp) => {
        if (orphanSet.has(rp.page.id)) {
          rp.fields.forEach((f) => result.orphanFields.push(f.id));
        }
      });
    }
  }

  return result;
}

// =====================================================
// 6. COMPOSED HOOK
// =====================================================

export function useConditionalLogic({
  fields,
  formData,
  pages,
}: UseConditionalLogicProps): UseConditionalLogicReturn {
  const visibleFields = useMemo(
    () => getVisibleFields(fields, formData),
    [fields, formData]
  );

  const { map: nextFieldMap, calculations: ruleCalculations } = useMemo(
    () => getNextFieldMapWithCalculations(visibleFields, formData),
    [visibleFields, formData]
  );

  const nextPageMap = useMemo(
    () => (pages && pages.length > 0 ? buildNextPageMap(pages, formData, fields) : {}),
    [pages, formData, fields]
  );

  const flowValidation = useMemo(
    () => validateFlowIntegrity(fields, pages),
    [fields, pages]
  );

  const resolvedPages = pages || [];

  return {
    visibleFields,
    nextFieldMap,
    nextPageMap,
    ruleCalculations,
    getNextField: (id) => nextFieldMap[id],
    getPreviousField: (history) => history[history.length - 1] || null,
    getNextPage: (id) => nextPageMap[id] ?? null,
    flowValidation,
    calculateProgress: (id) => calculatePathProgress(id, visibleFields, nextFieldMap),
    calculatePageProgress: (id) => calculatePagePathProgress(id, resolvedPages, nextPageMap),
  };
}

// Export utilities for testing/reuse
export { evaluateCondition, evaluateConditions, getVisibleFields, getNextFieldMap, validateFlowIntegrity, buildNextPageMap };
